import { differenceInDays } from 'date-fns';

export const ChatConfig = {
	SCROLL_THROTTLE_MS: 150,
	AUTO_SCROLL_TO_BOTTOM_TRIGGER_PX: 40,
	// (0, 1] value representing scroll progress remaining until ChatList should fetch next messages
	LOAD_NEXT_MESSAGES_TRIGGER_PERCENT: 0.3,
	TYPING_STATUS_THROTTLE_MS: 2500,
	TYPING_STATUS_DISPLAY_MS: 15000,
};

export const ChatBot = {
	MSG_CONTENT_INTRO: '👋 Hey! Welcome to Asaph, the worship team platform.',
	MSG_CONTENT_MEMBER_ONBOARD:
		'Help your team grow together and work better. Invite them now to get started! 👇\n',
	MSG_CONTENT_INVITED: (name: string) =>
		`Invite sent to ${name}! You can chat with people when they join your team.`,
	MSG_CONTENT_INVITED_CALL_TO_CHAT:
		'Until then, feel free to add more team members anytime! 👌',
};

export const Text = {
	UNKNOWN_ERROR_MESSAGE: 'Unknown error occured, please refresh the page',
};

export const Video = {
	STATS_UPDATE_THROTTLE_MS: 5000,
};

export const Thumbnail = {
	RESOLUTION_HIGH: { width: 1233, height: 1920 },
	RESOLUTION_LOW: { width: 514, height: 800 },
};

export const appTitle = 'Asaph';

export const Pricing = {
	MONTHLY_AUD: 28,
	MONTHLY_USD: 24,
	YEARLY_AUD: 252,
	YEARLY_USD: 216,
	MONTHLY: 'Monthly',
	YEARLY: 'Yearly',
	AUD: 'AUD',
	USD: 'USD',
};

export const SupportPricingTier1 = {
	YEARLY: 60,
	PRICE_PLAN_ID_PREFIX: 'Supporter-Support',
	AUD: 'AUD',
	USD: 'USD',
};

export const SupportPricingTier2 = {
	YEARLY: 240,
	PRICE_PLAN_ID_PREFIX: 'Supporter-Grow',
	AUD: 'AUD',
	USD: 'USD',
};

export const SupportPricingTier3 = {
	YEARLY: 480,
	PRICE_PLAN_ID_PREFIX: 'Supporter-Scale',
	AUD: 'AUD',
	USD: 'USD',
};

export const unknownDisplayName = 'Unknown';
export const unknownEmail = 'anonymized';

export const sendingMessageStatus = 'sending';

export const DASHBOARD_MAX_COLLECTIONS = 3;

export const PANEL_TRANSITION_TIMEOUT = 300;

export const INITIAL_MESSAGES_SIZE = 20;

// this controlls the number of songs in the "Add song" modal
export const INITIAL_SONGLIST_RESULT_SIZE = 20;

export enum LinkFrom {
	dashboard = 'dashboard',
	messages = 'messages',
	library = 'library',
}

export const termsOfServiceLink = 'https://asaph.io/terms-of-service';
export const privacyPolicyLink = 'https://asaph.io/privacy-policy';

export const DefaultKeys = [
	'C',
	'Db',
	'D',
	'Eb',
	'E',
	'F',
	'F#',
	'G',
	'Ab',
	'A',
	'Bb',
	'B',
];

export const DAYS_OF_WEEK = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

// Keep in sync with ALLOWED_SERVICE_SIZES in
// server/src/GraphQL/service/serviceResolver.ts
export const ALLOWED_SERVICE_SIZES = [
	'small',
	'medium',
	'large',
	'veryLarge',
	'mega',
] as const;

export type ServiceSize = (typeof ALLOWED_SERVICE_SIZES)[number];

const SERVICE_SIZE_LABELS: Record<ServiceSize, string> = {
	small: 'Small (40-200)',
	medium: 'Medium (200-450)',
	large: 'Large (400-800)',
	veryLarge: 'Very Large (800-1,800)',
	mega: 'Mega (1,800+)',
};

export const SERVICE_SIZE_OPTIONS = ALLOWED_SERVICE_SIZES.map((value) => ({
	label: SERVICE_SIZE_LABELS[value],
	value,
}));

export const normalizeServiceSize = (
	size: string | null | undefined,
): ServiceSize => {
	if (
		size &&
		(ALLOWED_SERVICE_SIZES as readonly string[]).includes(size)
	) {
		return size as ServiceSize;
	}
	return 'small';
};

export const REMOVED_SONG_NAME = 'Removed song';

export type RotationStatusWarningValidateFn = (
	datesLastPlayed: string[], // List of relevant ISO dates when the song was played
	comparisonISODate: string, // ISO date used for the comparison logic
) => boolean;

export const ROTATION_STATUSES: {
	key: string;
	description: string;
	warningDescription?: string;
	capitalisedLabel: string;
	warningValidateFn?: RotationStatusWarningValidateFn;
}[] = [
	{
		key: 'high',
		description: '2 or more times per month',
		warningDescription: 'it has been played more than 2 times ',
		capitalisedLabel: 'High',
	},
	{
		key: 'regular',
		description: 'Once per month',
		capitalisedLabel: 'Regular',
		warningDescription:
			'This song is on regular rotation, but has already been played or scheduled in the last month.', // <warning> It was last played <date>
		warningValidateFn: (datesLastPlayed, comparisonISODate) => {
			// If we return true, then it is valid, if false, then it's invalid so show a warning
			try {
				const lastPlayed = datesLastPlayed.splice(-1)[0];
				if (!lastPlayed) {
					// If it has been played less than 2 times, then its valid
					return true;
				}
				const dayDifference = differenceInDays(
					new Date(comparisonISODate),
					new Date(lastPlayed),
				);
				console.log(dayDifference, lastPlayed, comparisonISODate);
				return dayDifference < 28 ? false : true;
			} catch (err) {
				console.error(err);
			}
			return true;
		},
	},
	{
		key: 'low',
		description: 'Once every few months',
		capitalisedLabel: 'Low',
		warningDescription:
			'This song is on low rotation, but has already been played or scheduled in the last few months.', // <warning> It was last played <date>
		warningValidateFn: (datesLastPlayed, comparisonISODate) => {
			// If we return true, then it is valid, if false, then it's invalid so show a warning
			try {
				const lastPlayed = datesLastPlayed.splice(-1)[0];
				if (!lastPlayed) {
					// If it has been played less than 2 times, then its valid
					return true;
				}
				const dayDifference = differenceInDays(
					new Date(comparisonISODate),
					new Date(lastPlayed),
				);
				return dayDifference < 90 ? false : true;
			} catch (err) {
				console.error(err);
			}
			return true;
		},
	},
	{
		key: 'occasional',
		description: '1-2 times per year',
		capitalisedLabel: 'Occasional',
		warningDescription:
			'This song is on occasional rotation, but has already been played or scheduled twice in the last year.', // <warning> It was last played <date>
		warningValidateFn: (datesLastPlayed, comparisonISODate) => {
			// If we return true, then it is valid, if false, then it's invalid so show a warning
			try {
				const lastPlayed = datesLastPlayed.splice(-1)[0];
				if (!lastPlayed) {
					// If it has been played less than 2 times, then its valid
					return true;
				}
				const dayDifference = differenceInDays(
					new Date(comparisonISODate),
					new Date(lastPlayed),
				);
				return dayDifference < 365 ? false : true;
			} catch (err) {
				console.error(err);
			}
			return true;
		},
	},
	{
		key: 'seasonal',
		description: 'Best for items, Easter and Christmas',
		capitalisedLabel: 'Seasonal',
	},
	{
		key: 'rest',
		description: 'Out of rotation',
		capitalisedLabel: 'Rest',
		warningDescription: 'This song is currently out of rotation.', // <warning> It was last played <date>
		warningValidateFn: () => {
			// If we return true, then it is valid, if false, then it's invalid so show a warning
			return false;
		},
	},
];

export const ENERGY_TAGS = [
	{ label: 'upbeat', capitalisedLabel: 'Upbeat' },
	{ label: 'building', capitalisedLabel: 'Building' },
	{ label: 'settling', capitalisedLabel: 'Settling' },
	{ label: 'reflective', capitalisedLabel: 'Reflective' },
];

export const EMOTION_TAGS = [
	{ label: 'joy', capitalisedLabel: 'Joy' },
	{ label: 'trust', capitalisedLabel: 'Trust' },
	{ label: 'fear', capitalisedLabel: 'Fear' },
	{ label: 'surprise', capitalisedLabel: 'Surprise' },
	{ label: 'sadness', capitalisedLabel: 'Sadness' },
	{ label: 'disgust', capitalisedLabel: 'Disgust' },
	{ label: 'anger', capitalisedLabel: 'Anger' },
	{ label: 'anticipation', capitalisedLabel: 'Anticipation' },
];

export const GOSPEL_TAGS = [
	{ label: 'glory of god', capitalisedLabel: 'Glory of God' },
	{ label: 'gravity of sin', capitalisedLabel: 'Gravity of Sin' },
	{ label: 'grandeur of grace', capitalisedLabel: 'Grandeur of Grace' },
];

export const GOSPEL_TAGS_SHORT = [
	{ label: 'god', capitalisedLabel: 'God' },
	{ label: 'sin', capitalisedLabel: 'Sin' },
	{ label: 'grace', capitalisedLabel: 'Grace' },
];

export const SONG_TYPE_TAGS = [
	{ label: 'chorus', capitalisedLabel: 'Chorus' },
	{ label: 'hymn', capitalisedLabel: 'Hymn' },
];

export const PERSONAL_STEP_LABEL_MAP = [
	'Not at all',
	'Meh',
	'Good',
	'Great',
	'Amazing',
];
export const TEAM_STEP_LABEL_MAP = [
	'Unprepared',
	'Meh',
	'Good',
	'Great',
	'Crushed it',
];
export const CONGREGATIONAL_STEP_LABEL_MAP = [
	'Struggling',
	'Flat',
	'Good',
	'Great',
	'Belting it',
];

export const ASAPH_BOT_USER_ID = '0';
export const ASAPH_BOT_DISPLAY_NAME = 'Asaph';
export const REMINDERS_LOCAL_STORAGE_KEY = 'asahph-reminders';
export const ENV_OVERRIDE_STORAGE_KEY = 'asahph-env-override';
export const DRAFT_SETLISTS_STORAGE_KEY = 'asahph-draft-setlists';

export enum SentryTag {
	DEPLOYMENT = 'deployment',
}

export enum SentryDeploymentContext {
	WEB = 'web',
	NATIVE_APP = 'native-app',
	EXTENSION = 'extension-pco',
}

export enum UserMilestones {
	PROFILE_SET_UP = 'profile_set_up',
	ONBOARDING_COMPLETED = 'onboarding_completed',
	GIVEN_FIRST_FEEDBACK = 'given_first_feedback',
}

export enum TeamMilestones {
	ADDED_INITIAL_SONGS = 'added_initial_songs',
	CREATED_FIRST_SETLIST = 'created_first_setlist',
}
